/**
 * NotificationsSettings — per-user alarm notification preferences form.
 *
 * Contract invariants (Cloud Alarm Overhaul, Phase 7):
 *
 * 1. Pane-of-glass: this form writes ONLY to alarms.notification_preferences.
 *    It MUST NOT render ack/clear/resolve/mute/test-notification controls.
 *    Alarm state lives on the edge and is mirrored here read-only.
 *
 * 2. 3-tier severity: the min-severity radio group offers exactly
 *    warning / critical / off. No 'urgent'. No 'info' (info-tier state
 *    events are SLD-bound, not notification-bound).
 *
 * 3. In-app channel is always on. The UI surfaces an "always on" pill but
 *    no toggle control for in-app.
 *
 * 4. Empty site_scope → null, not []. Null means "all sites I have access
 *    to"; [] would mean "no sites", which is a usability footgun. The
 *    backend matcher (Phase 6) treats them the same way.
 *
 * 5. PUT payload omits user_id, customer_id, created_at, updated_at.
 *    Backend derives identity from JWT; timestamps are server-managed.
 *    The NotificationPreferencesUpdate type in notificationPrefsApi
 *    enforces this at compile time.
 *
 * 6. Every authenticated user — including non-admins — edits their own
 *    prefs. There is no "manage someone else's prefs" view.
 *
 * 7. quiet_hours_start/end are persisted round-trip for forward compat
 *    but NOT surfaced in the UI (Phase 6 does not consult them yet).
 */

import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Bell, Mail, MessageSquare, Loader2, Save, CheckCircle2, AlertCircle, ShieldCheck } from 'lucide-react';
import {
  useNotificationPreferences,
  NOTIFICATION_PREFS_QUERY_KEY,
} from '../../hooks/useNotificationPreferences';
import { useSites } from '../../hooks/useSites';
import { notificationPrefsApi, type MinSeverity } from '../../services/notificationPrefsApi';
import { CONSENT_COPY_VERSION, SMS_PRIVACY_URL, SMS_TERMS_URL, smsProgramName } from '../../constants/smsConsent';
import { tenantName } from '../../config/tenant';
import { useAuthStore } from '../../store/authStore';

type VerifyStage = 'idle' | 'awaiting-code' | 'verified';

/** Map a backend verification error code to a user-facing string. */
function verifyErrorMessage(code: string, attemptsRemaining?: number): string {
  switch (code) {
    case 'invalid_phone_number':
      return 'Enter a valid US phone number (10 digits).';
    case 'verification_rate_limited':
      return 'Too many code requests for this number. Try again in an hour.';
    case 'invalid_code_format':
      return 'Code must be 6 digits.';
    case 'no_active_code':
      return 'No active verification code. Send a new one.';
    case 'code_expired':
      return 'That code expired. Send a new one.';
    case 'max_attempts_reached':
      return 'Too many incorrect attempts. Send a new code.';
    case 'incorrect_code':
      return attemptsRemaining !== undefined
        ? `Incorrect code. ${attemptsRemaining} attempt${attemptsRemaining === 1 ? '' : 's'} left.`
        : 'Incorrect code.';
    default:
      return 'Verification failed. Try again.';
  }
}

const SEVERITY_OPTIONS: ReadonlyArray<{ value: MinSeverity; label: string; hint: string }> = [
  { value: 'warning', label: 'All (critical + warning)', hint: 'Notify me for warnings and criticals.' },
  { value: 'critical', label: 'Critical only', hint: 'Notify me for critical alarms only.' },
  { value: 'off', label: 'Off', hint: 'Do not send any email or SMS notifications.' },
];

/**
 * Pragmatic email-shape check. The backend remains the source of truth; this
 * just catches obvious typos before a save that would otherwise leave the
 * dispatcher with an address it can't deliver to.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function NotificationsSettings() {
  const { preferences, isLoading, isError, save, isSaving, saveError } =
    useNotificationPreferences();
  const sitesQuery = useSites();
  const qc = useQueryClient();

  const [emailEnabled, setEmailEnabled] = useState(false);
  const [emailDraft, setEmailDraft] = useState('');
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [minSeverity, setMinSeverity] = useState<MinSeverity>('critical');
  const [siteScope, setSiteScope] = useState<string[] | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Phone-verification (issue #264) state.
  const [phoneDraft, setPhoneDraft] = useState('');
  const [stage, setStage] = useState<VerifyStage>('idle');
  const [code, setCode] = useState('');
  const [verifyError, setVerifyError] = useState<string | null>(null);
  /**
   * Set when the backend issued a code but could not actually send the SMS
   * (issue #745 item 1). Distinct from `verifyError`: the request SUCCEEDED and
   * the code exists, so the entry field stays open — a dev/test environment
   * completes verification by reading the code from the DB. What must not
   * happen is a real user being moved to "enter the code we texted you" with no
   * text on its way and nothing on screen saying so.
   */
  const [smsNotDispatched, setSmsNotDispatched] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  // Transient hint shown when a user clicks the consent checkbox before the
  // phone is verified (issue #342). A disabled <input> swallows clicks, so the
  // hint is driven from the wrapping <label>'s onClick.
  const [showConsentHint, setShowConsentHint] = useState(false);

  // Hydrate local state from the server prefs on load (and on refetch).
  useEffect(() => {
    if (!preferences) return;
    setEmailEnabled(preferences.email_enabled);
    // Default a missing address to the user's login email so the common case
    // (alert me at the address I sign in with) is one toggle away (#505).
    setEmailDraft(preferences.email_address ?? useAuthStore.getState().user?.email ?? '');
    setSmsEnabled(preferences.sms_enabled);
    setMinSeverity(preferences.min_severity);
    setSiteScope(preferences.site_scope);
    setPhoneDraft(preferences.phone_number ?? '');
    setStage(preferences.phone_verified_at ? 'verified' : 'idle');
  }, [preferences]);

  /**
   * The SMS toggle is enabled iff (a) the user has a verified phone on file,
   * AND (b) the typed `phoneDraft` matches the verified `phone_number`. Editing
   * the phone field after verification disables SMS until re-verification.
   */
  const phoneChangedSinceVerification = useMemo(() => {
    if (!preferences?.phone_verified_at) return false;
    return phoneDraft.trim() !== (preferences.phone_number ?? '');
  }, [preferences?.phone_verified_at, preferences?.phone_number, phoneDraft]);

  const smsToggleDisabled =
    stage !== 'verified' || phoneChangedSinceVerification;

  // If the user edits phone away from the verified value, force SMS off.
  useEffect(() => {
    if (smsToggleDisabled && smsEnabled) setSmsEnabled(false);
  }, [smsToggleDisabled, smsEnabled]);

  // Once the consent checkbox becomes enabled (phone verified), drop any
  // stale "verify your phone first" hint.
  useEffect(() => {
    if (!smsToggleDisabled) setShowConsentHint(false);
  }, [smsToggleDisabled]);

  const onSendCode = async () => {
    setVerifyError(null);
    setSmsNotDispatched(false);
    setIsSendingCode(true);
    try {
      const res = await notificationPrefsApi.verifyPhoneStart(phoneDraft);
      // Only an explicit `false` warns. An older backend omits the field, and
      // treating `undefined` as failure would warn on every send.
      setSmsNotDispatched(res.sms_dispatched === false);
      setStage('awaiting-code');
      setCode('');
    } catch (err: any) {
      const errorCode = err?.response?.data?.error;
      setVerifyError(verifyErrorMessage(errorCode || 'unknown'));
    } finally {
      setIsSendingCode(false);
    }
  };

  const onVerifyCode = async () => {
    setVerifyError(null);
    setIsVerifyingCode(true);
    try {
      await notificationPrefsApi.verifyPhoneConfirm(code);
      // Server now has phone_verified_at = NOW(). Refetch prefs so the hook
      // returns truthy phone_verified_at and the SMS toggle becomes enabled.
      await qc.invalidateQueries({ queryKey: NOTIFICATION_PREFS_QUERY_KEY });
      setStage('verified');
      setCode('');
    } catch (err: any) {
      const errorCode = err?.response?.data?.error;
      const attemptsRemaining = err?.response?.data?.attempts_remaining;
      setVerifyError(verifyErrorMessage(errorCode || 'unknown', attemptsRemaining));
      // After max-attempts the active code is invalidated; bounce back to idle.
      if (errorCode === 'max_attempts_reached' || errorCode === 'code_expired') {
        setStage('idle');
        setCode('');
      }
    } finally {
      setIsVerifyingCode(false);
    }
  };

  const onChangePhone = () => {
    // Allow the user to edit a verified phone — drops them back to idle.
    setStage('idle');
    setCode('');
    setVerifyError(null);
  };

  const sites = sitesQuery.data?.sites ?? [];

  // site is considered in-scope if the scope is null (all accessible sites)
  // or the explicit array includes its UUID.
  const isSiteSelected = (siteId: string): boolean =>
    siteScope === null || siteScope.includes(siteId);

  const toggleSite = (siteId: string) => {
    const currentExplicit = siteScope ?? sites.map((s) => s.site_id);
    const next = currentExplicit.includes(siteId)
      ? currentExplicit.filter((s) => s !== siteId)
      : [...currentExplicit, siteId];

    // If the user has re-selected every site, collapse back to null.
    // If the user has deselected every site, collapse to null (empty = null
    // per contract; [] would mean "no sites" and silently block all dispatch).
    if (next.length === 0 || next.length === sites.length) {
      setSiteScope(null);
      return;
    }
    setSiteScope(next);
  };

  // Email address gating: a user can't enable email without a valid address on
  // file (the dispatcher would otherwise record failed/no_email_address).
  const trimmedEmail = emailDraft.trim();
  const emailMissing = emailEnabled && trimmedEmail === '';
  const emailInvalid = emailEnabled && trimmedEmail !== '' && !EMAIL_RE.test(trimmedEmail);
  const emailBlocksSave = emailMissing || emailInvalid;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (emailBlocksSave) return;
    try {
      await save({
        email_enabled: emailEnabled,
        email_address: trimmedEmail === '' ? null : trimmedEmail,
        sms_enabled: smsEnabled,
        min_severity: minSeverity,
        site_scope: siteScope,
        consent_rendered: { version: CONSENT_COPY_VERSION, program_name: smsProgramName(tenantName) },
      });
      setSavedAt(Date.now());
    } catch {
      // Error surfaced inline via saveError; see the alert block below.
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-ems-green" aria-label="Loading" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-ems-red mx-auto mb-2" />
          <p className="text-sm text-ems-text-muted">Failed to load notification preferences.</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-3">
        <Bell className="w-5 h-5 text-ems-green" />
        <div>
          <h1 className="text-lg font-bold text-ems-text-primary">Alarm notifications</h1>
          <p className="text-sm text-ems-text-muted">
            Choose how and when you&apos;re notified when alarms fire at your sites. In-app
            notifications are always on.
          </p>
        </div>
      </div>

      {/* Delivery channels */}
      <section className="bg-ems-bg-card rounded-2xl border border-ems-border">
        <div className="px-6 py-4 border-b border-ems-border">
          <h2 className="text-sm font-semibold text-ems-text-primary">Delivery channels</h2>
          <p className="text-xs text-ems-text-muted mt-0.5">
            In-app is always enabled. Email and SMS are per-user opt-in.
          </p>
        </div>
        <div className="divide-y divide-ems-border">
          {/* In-app: always on, no toggle */}
          <div className="px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bell className="w-4 h-4 text-ems-green" aria-hidden="true" />
              <div>
                <p className="text-sm font-medium text-ems-text-primary">In-app (bell icon)</p>
                <p className="text-xs text-ems-text-muted">Always on for every user.</p>
              </div>
            </div>
            <span className="px-2 py-0.5 bg-ems-green/15 text-ems-green border border-ems-green/30 rounded-full text-[10px] font-semibold">
              ALWAYS ON
            </span>
          </div>

          {/* Email channel — toggle + editable address. The address input lives
              OUTSIDE the checkbox <label> so typing in it never toggles email. */}
          <div className="px-6 py-4">
            <label className="flex items-center justify-between cursor-pointer">
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-ems-text-muted" aria-hidden="true" />
                <div>
                  <p className="text-sm font-medium text-ems-text-primary">Email</p>
                  <p className="text-xs text-ems-text-muted">
                    Receive an email when an alarm fires on one of your sites.
                  </p>
                </div>
              </div>
              <input
                type="checkbox"
                aria-label="Email"
                checked={emailEnabled}
                onChange={(e) => setEmailEnabled(e.target.checked)}
                className="w-4 h-4 accent-ems-green"
              />
            </label>

            <div className="mt-3 ml-7 space-y-1">
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                aria-label="Notification email address"
                placeholder="you@company.com"
                value={emailDraft}
                onChange={(e) => setEmailDraft(e.target.value)}
                className="w-full px-3 py-1.5 text-sm rounded-lg bg-ems-bg-card border border-ems-border text-ems-text-primary placeholder:text-ems-text-muted focus:outline-none focus:border-ems-green"
              />
              {emailMissing && (
                <p role="status" className="text-[11px] text-ems-amber flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Add an email address to receive email alerts.
                </p>
              )}
              {emailInvalid && (
                <p role="alert" className="text-[11px] text-ems-red flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Enter a valid email address.
                </p>
              )}
            </div>
          </div>

          {/* SMS — CTIA / Twilio A2P 10DLC opt-in row (issues #264, #342) */}
          <div className="px-6 py-4">
            {/* Row header */}
            <div className="flex items-center gap-3">
              <MessageSquare className="w-4 h-4 text-ems-text-muted" aria-hidden="true" />
              <div>
                <p className="text-sm font-medium text-ems-text-primary">SMS</p>
                <p className="text-xs text-ems-text-muted">
                  Receive a text when an alarm fires on one of your sites.
                </p>
              </div>
            </div>

            {/* Phone field + verification flow */}
            <div className="mt-3 ml-7 space-y-2">
              {stage === 'verified' && !phoneChangedSinceVerification ? (
                <div className="flex items-center gap-2 text-sm">
                  <ShieldCheck className="w-4 h-4 text-ems-green" aria-hidden="true" />
                  <span className="font-medium text-ems-green">Verified</span>
                  <span className="text-ems-text-primary">{preferences?.phone_number}</span>
                  <span className="text-ems-text-muted" aria-hidden="true">—</span>
                  <button
                    type="button"
                    onClick={onChangePhone}
                    className="text-xs text-ems-blue hover:underline"
                  >
                    change number
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    type="tel"
                    inputMode="tel"
                    aria-label="Phone number"
                    placeholder="(555) 555-0123"
                    value={phoneDraft}
                    onChange={(e) => {
                      setPhoneDraft(e.target.value);
                      if (stage === 'awaiting-code') {
                        setStage('idle');
                        setCode('');
                      }
                    }}
                    className="flex-1 px-3 py-1.5 text-sm rounded-lg bg-ems-bg-card border border-ems-border text-ems-text-primary placeholder:text-ems-text-muted focus:outline-none focus:border-ems-green"
                  />
                  <button
                    type="button"
                    onClick={onSendCode}
                    disabled={isSendingCode || phoneDraft.trim().length < 10}
                    className="px-3 py-1.5 text-xs rounded-lg bg-ems-forest text-ems-green border border-ems-green/20 hover:bg-ems-forest/80 disabled:opacity-50 transition-colors"
                  >
                    {isSendingCode ? (
                      <Loader2 className="w-3 h-3 animate-spin inline" />
                    ) : (
                      'Send code'
                    )}
                  </button>
                </div>
              )}

              {phoneChangedSinceVerification && (
                <p
                  role="status"
                  className="text-[11px] text-ems-amber flex items-center gap-1"
                >
                  <AlertCircle className="w-3 h-3" />
                  Phone changed — re-verify to enable SMS.
                </p>
              )}

              {stage === 'awaiting-code' && (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    aria-label="Verification code"
                    placeholder="6-digit code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="px-3 py-1.5 text-sm rounded-lg bg-ems-bg-card border border-ems-border text-ems-text-primary placeholder:text-ems-text-muted focus:outline-none focus:border-ems-green"
                  />
                  <button
                    type="button"
                    onClick={onVerifyCode}
                    disabled={isVerifyingCode || code.length !== 6}
                    className="px-3 py-1.5 text-xs rounded-lg bg-ems-green text-ems-bg-card font-medium hover:bg-ems-green/90 disabled:opacity-50 transition-colors"
                  >
                    {isVerifyingCode ? <Loader2 className="w-3 h-3 animate-spin inline" /> : 'Verify'}
                  </button>
                  <button
                    type="button"
                    onClick={onSendCode}
                    disabled={isSendingCode}
                    className="px-2 py-1 text-xs text-ems-blue hover:underline disabled:opacity-50"
                  >
                    Resend
                  </button>
                </div>
              )}

              {verifyError && (
                <p role="alert" className="text-[11px] text-ems-red flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {verifyError}
                </p>
              )}

              {/*
                Gated on `stage`, not cleared at each reset. The banner is only
                ever true while the user is waiting for a code, and there are FOUR
                paths back out of that state (verify success, max-attempts bounce,
                "Change phone", and editing the number inline). Clearing the flag
                at each one leaves the next path added to reintroduce the bug —
                and it is loudest in exactly the environment the flag exists for,
                where a dev completes verification by reading the code from the DB
                and would otherwise be told forever that texting is unavailable.
              */}
              {smsNotDispatched && stage === 'awaiting-code' && (
                <p role="alert" className="text-[11px] text-ems-amber flex items-start gap-1">
                  <AlertCircle className="w-3 h-3 shrink-0 mt-px" />
                  <span>
                    Text messaging is unavailable right now, so the code could not be
                    sent. Contact support rather than waiting for a message.
                  </span>
                </p>
              )}
            </div>

            {/* Consent checkbox + CTIA / Twilio-required disclosures (issue #342).
                The checkbox is unchecked on first render for any user who has
                not previously opted in (smsEnabled hydrates from sms_enabled,
                which defaults FALSE) and stays disabled until the phone is
                verified. */}
            <div className="mt-4 ml-7">
              <label
                className={`flex items-start gap-3 ${
                  smsToggleDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
                }`}
                onClick={() => {
                  if (smsToggleDisabled) setShowConsentHint(true);
                }}
              >
                <input
                  type="checkbox"
                  checked={smsEnabled}
                  disabled={smsToggleDisabled}
                  onChange={(e) => setSmsEnabled(e.target.checked)}
                  className="mt-0.5 w-4 h-4 flex-shrink-0 accent-ems-green disabled:opacity-40 disabled:cursor-not-allowed"
                />
                <span className="text-xs text-ems-text-secondary leading-relaxed">
                  <strong className="text-ems-text-primary">Yes, I agree</strong> to receive
                  automated text messages from{' '}
                  <strong className="text-ems-text-primary">{smsProgramName(tenantName)}</strong> about
                  fault, warning, and project-status events for my {tenantName} projects.{' '}
                  <strong className="text-ems-text-primary">
                    Message frequency varies based on project activity. Message and data rates may
                    apply.
                  </strong>{' '}
                  Reply <strong className="text-ems-text-primary">HELP</strong> for help or{' '}
                  <strong className="text-ems-text-primary">STOP</strong> to cancel at any time.
                  Consent is not a condition of any purchase. See our{' '}
                  <a
                    href={SMS_PRIVACY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-ems-blue hover:underline"
                  >
                    SMS Privacy Policy
                  </a>{' '}
                  and{' '}
                  <a
                    href={SMS_TERMS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-ems-blue hover:underline"
                  >
                    SMS Terms &amp; Conditions
                  </a>
                  .
                </span>
              </label>

              {showConsentHint && smsToggleDisabled && (
                <p
                  role="status"
                  className="mt-1.5 ml-7 text-[11px] text-ems-amber flex items-center gap-1"
                >
                  <AlertCircle className="w-3 h-3" />
                  Verify your phone number above first.
                </p>
              )}

              <p className="mt-2 ml-7 text-[11px] text-ems-text-muted">
                You can opt out any time by unchecking this box, or by replying STOP to any DJIN
                EMS text message.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Minimum severity */}
      <section className="bg-ems-bg-card rounded-2xl border border-ems-border">
        <div className="px-6 py-4 border-b border-ems-border">
          <h2 className="text-sm font-semibold text-ems-text-primary">Notify me for</h2>
          <p className="text-xs text-ems-text-muted mt-0.5">
            Minimum alarm severity that triggers a notification.
          </p>
        </div>
        <div
          role="radiogroup"
          aria-label="Minimum severity"
          className="px-6 py-4 space-y-2"
        >
          {SEVERITY_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                minSeverity === opt.value
                  ? 'border-ems-green bg-ems-green/5'
                  : 'border-ems-border hover:border-ems-text-muted'
              }`}
            >
              <input
                type="radio"
                name="min-severity"
                value={opt.value}
                checked={minSeverity === opt.value}
                onChange={() => setMinSeverity(opt.value)}
                aria-label={opt.label}
                className="mt-0.5 accent-ems-green"
              />
              <div>
                <p className="text-sm font-medium text-ems-text-primary">{opt.label}</p>
                <p className="text-xs text-ems-text-muted">{opt.hint}</p>
              </div>
            </label>
          ))}
        </div>
      </section>

      {/* Site scope */}
      <section className="bg-ems-bg-card rounded-2xl border border-ems-border">
        <div className="px-6 py-4 border-b border-ems-border">
          <h2 className="text-sm font-semibold text-ems-text-primary">Sites</h2>
          <p className="text-xs text-ems-text-muted mt-0.5">
            Alarms from these sites trigger notifications. When all sites are selected (or none
            are), we default to all sites you have access to.
          </p>
        </div>
        <div className="px-6 py-4">
          {sitesQuery.isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin text-ems-green" />
          ) : sites.length === 0 ? (
            <p className="text-xs text-ems-text-muted">
              You don&apos;t have access to any sites yet.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {sites.map((s) => {
                const selected = isSiteSelected(s.site_id);
                return (
                  <button
                    type="button"
                    key={s.site_id}
                    onClick={() => toggleSite(s.site_id)}
                    aria-pressed={selected}
                    className={`px-3 py-1.5 rounded-full border text-xs transition-colors ${
                      selected
                        ? 'bg-ems-blue/10 border-ems-blue/30 text-ems-blue'
                        : 'border-ems-border text-ems-text-muted hover:border-ems-text-muted'
                    }`}
                  >
                    {s.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Save error */}
      {saveError && (
        <div
          role="alert"
          className="flex items-start gap-2 p-3 rounded-lg bg-ems-red/10 border border-ems-red/30 text-xs text-ems-red"
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Could not save preferences.</p>
            <p className="opacity-80">
              {saveError instanceof Error ? saveError.message : 'Unknown error.'}
            </p>
          </div>
        </div>
      )}

      {/* Save bar */}
      <div className="flex items-center justify-end gap-3">
        {savedAt && !isSaving && !saveError && (
          <span className="flex items-center gap-1 text-xs text-ems-green">
            <CheckCircle2 className="w-4 h-4" />
            Saved.
          </span>
        )}
        <button
          type="submit"
          disabled={isSaving || emailBlocksSave}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-ems-forest text-ems-green border border-ems-green/20 text-sm font-medium hover:bg-ems-forest/80 disabled:opacity-50 transition-colors"
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Save preferences
        </button>
      </div>
    </form>
  );
}
